import { useCallback, useEffect, useRef, useState } from "react";
import type { DirectMessage } from "@/lib/types";
import { useUI } from "@/lib/theme";

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(iso: string, lang: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
    month: "short",
    day: "numeric",
  });
}

function isSameDay(a: string, b: string): boolean {
  return a.slice(0, 10) === b.slice(0, 10);
}

interface Props {
  viewerRole: "coach" | "athlete";
  fetchMessages: () => Promise<{ messages: DirectMessage[] }>;
  sendMessage: (body: string) => Promise<{ message: DirectMessage }>;
  /** Interlocutor (coach para el atleta, atleta para el coach). Se muestra en las burbujas entrantes. */
  peer?: { name: string; profilePic?: string | null };
}

export default function DirectChat({ viewerRole, fetchMessages, sendMessage, peer }: Props) {
  const { t, lang } = useUI();
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    fetchMessages().then((r) => setMessages(r.messages)).catch(() => {});
  }, [fetchMessages]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    try {
      const res = await sendMessage(text);
      setMessages((prev) => [...prev, res.message]);
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="flex flex-col rounded-xl border border-line bg-panel/70">
      {/* Lista de mensajes */}
      <div
        ref={scrollRef}
        className="flex h-[40vh] min-h-[240px] max-h-[480px] flex-col gap-1 overflow-y-auto p-4"
      >
        {messages.length === 0 && (
          <p className="m-auto text-sm text-muted">{t("msg_empty")}</p>
        )}
        {messages.map((msg, i) => {
          const isMe = msg.author_role === viewerRole;
          const showDate =
            i === 0 || !isSameDay(messages[i - 1]!.created_at, msg.created_at);
          return (
            <div key={msg.id}>
              {showDate && (
                <p className="my-2 text-center text-[11px] text-muted">
                  {formatDate(msg.created_at, lang)}
                </p>
              )}
              <div className={`flex ${isMe ? "justify-end" : "justify-start"} items-end gap-2`}>
                {!isMe && peer && (
                  <div className="shrink-0">
                    {peer.profilePic ? (
                      <img
                        src={peer.profilePic}
                        alt={peer.name}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/20 text-[9px] font-semibold text-accent">
                        {peer.name.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                )}
                <div className="max-w-[75%]">
                  {!isMe && peer && (
                    <p className="mb-0.5 text-[10px] text-muted">{peer.name}</p>
                  )}
                  <div
                    className={`rounded-2xl px-3 py-1.5 text-sm leading-relaxed ${
                      isMe
                        ? "bg-accent text-white"
                        : "border border-line bg-panel2/60 text-fg"
                    }`}
                  >
                    <p>{msg.body}</p>
                    <p
                      className={`mt-0.5 text-right text-[10px] ${
                        isMe ? "text-white/60" : "text-muted"
                      }`}
                    >
                      {formatTime(msg.created_at)}
                      {isMe && msg.read_at && " ✓"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="border-t border-line p-3">
        <form
          onSubmit={(e) => { e.preventDefault(); void send(); }}
          className="flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
            }}
            rows={1}
            placeholder={t("msg_placeholder")}
            className="max-h-20 min-h-[36px] flex-1 resize-none rounded-xl border border-line bg-panel px-3 py-2 text-sm text-fg outline-none transition focus:border-accent"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-60"
          >
            {t("msg_send")}
          </button>
        </form>
      </div>
    </section>
  );
}
